// ═════════════════════════════════════════════════════════════════════════════
//  CONFETI
//
//  Para el momento en que el alumno cierra una lección entera y vuelve al
//  coach. Es la única cosa de toda la app que existe solo para dar una alegría,
//  y por eso hay que tener cuidado con dos cosas:
//
//  ── QUIEN LO DISPARA ────────────────────────────────────────────────────────
//  Lo dispara el DATO —una lección cerrada al 100% hace menos de tres horas—,
//  nunca lo que diga el coach. Si dependiera del modelo, algún día saldría
//  confeti por una clase a medias, y una celebración por algo que no hiciste es
//  peor que no celebrar nada: te dice que el sistema no se entera.
//
//  ── SIN LIBRERIA ────────────────────────────────────────────────────────────
//  Un paquete de confeti son decenas de kilobytes que se descargan siempre
//  para algo que se ve tres segundos. Esto son treinta líneas y se va con el
//  componente.
//
//  ── Y SE APAGA PARA QUIEN LO PIDE ───────────────────────────────────────────
//  Con `prefers-reduced-motion` no se pinta nada. No es un detalle de cortesía:
//  a quien tiene sensibilidad al movimiento, una pantalla llena de cosas
//  cayendo le sienta mal de verdad.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from "react";
import "./Confeti.css";

const COLORES = ["#ffff00", "#00e5ff", "#ff5e7e", "#7c5cff", "#22c55e", "#ffffff"];

export default function Confeti({ piezas = 90, duracion = 3200, onFin }) {
  const [vivo, setVivo] = useState(true);

  const reducido = useMemo(
    () => typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  // Cada pieza se calcula UNA vez: si se recalcularan en cada repintado, el
  // confeti daría saltos cada vez que llega un mensaje nuevo.
  const trozos = useMemo(() => Array.from({ length: piezas }, (_, i) => ({
    id: i,
    izq: Math.random() * 100,
    color: COLORES[i % COLORES.length],
    retraso: Math.random() * 0.9,
    caida: 2.1 + Math.random() * 1.3,
    giro: 360 + Math.random() * 720,
    ancho: 6 + Math.random() * 6,
    alto: 9 + Math.random() * 8,
    deriva: (Math.random() - 0.5) * 160,
    redondo: i % 4 === 0,
  })), [piezas]);

  //  ── EL RELOJ NO SE PUEDE REINICIAR SOLO ────────────────────────────────
  //  `onFin` llega siempre como una funcion escrita en el sitio de la llamada,
  //  asi que es una funcion NUEVA cada vez que se repinta quien nos usa. Si el
  //  efecto dependiera de ella, cada repintado del padre cancelaria el reloj y
  //  lo volveria a poner en cero: en una pantalla viva —el chat del coach, el
  //  recorrido hablando— el confeti no se iba nunca.
  //
  //  Lo cazo la bateria del recorrido: seguia habiendo piezas cayendo cuatro
  //  segundos despues de una animacion de tres. Y una capa a pantalla completa
  //  que no se desmonta se queda por encima del resto de la sesion.
  //
  //  Se guarda en un `ref` para poder llamar SIEMPRE a la ultima version sin
  //  que su identidad entre en las dependencias.
  const alFinal = useRef(onFin);
  alFinal.current = onFin;

  useEffect(() => {
    if (reducido) { alFinal.current?.(); return; }
    const t = setTimeout(() => { setVivo(false); alFinal.current?.(); }, duracion);
    return () => clearTimeout(t);
  }, [duracion, reducido]);

  if (reducido || !vivo) return null;

  return (
    <div className="cfti" aria-hidden="true">
      {trozos.map(t => (
        <span
          key={t.id}
          className={`cfti-pieza${t.redondo ? " cfti-redonda" : ""}`}
          style={{
            left: `${t.izq}%`,
            background: t.color,
            width: `${t.ancho}px`,
            height: `${t.alto}px`,
            animationDelay: `${t.retraso}s`,
            animationDuration: `${t.caida}s`,
            "--cfti-giro": `${t.giro}deg`,
            "--cfti-deriva": `${t.deriva}px`,
          }}
        />
      ))}
    </div>
  );
}
